import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Container, Plus, Trash2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { suppliersService, type Supplier } from '@/services/suppliersService';
import {
  logisticsCatalogService,
  type LogisticsAssignmentRole,
  type LogisticsUnitAssignment,
  type LogisticsUnitKind,
  type LogisticsUnitTemplate,
  type LogisticsUnitTemplatePayload,
} from './logisticsCatalogService';
import { presetsForKind, type LogisticsUnitPreset } from './logisticsUnitPresets';
import { SupplierSearchSelect } from './SupplierSearchSelect';

const inputClass =
  'w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-stocker-primary focus:outline-none focus:ring-1 focus:ring-stocker-primary';
const btnPrimaryClass =
  'inline-flex items-center justify-center rounded-md bg-stocker-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50';
const btnSecondaryClass =
  'inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50';
const btnGhostClass =
  'inline-flex items-center gap-1.5 text-sm font-medium text-stocker-primary hover:underline';

const EMPTY_FORM: LogisticsUnitTemplatePayload = {
  code: '',
  name: '',
  description: '',
  kind: 'CONTAINER',
  active: true,
  internal_length_mm: null,
  internal_width_mm: null,
  internal_height_mm: null,
  volume_utilization_factor: 1,
  max_payload_kg: null,
  weight_utilization_factor: 1,
  minimum_fill_pct: null,
  maximum_fill_pct: null,
  capacity_binding_rule: 'BOTH',
  notes: '',
};

function errorMessage(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (e && typeof e === 'object' && 'message' in e) {
    const msg = (e as { message?: unknown }).message;
    if (typeof msg === 'string' && msg.trim()) return msg;
  }
  return String(e);
}

function numOrNull(raw: string): number | null {
  const t = raw.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function LogisticsCatalogPage() {
  const { t } = useTranslation();
  const [templates, setTemplates] = useState<LogisticsUnitTemplate[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [form, setForm] = useState<LogisticsUnitTemplatePayload>(EMPTY_FORM);
  const [assignments, setAssignments] = useState<LogisticsUnitAssignment[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [assignSupplierId, setAssignSupplierId] = useState<string>('');
  const [assignRole, setAssignRole] = useState<LogisticsAssignmentRole>('DEFAULT_CONTAINER');
  const [kindFilter, setKindFilter] = useState<LogisticsUnitKind | ''>('');
  const [presetId, setPresetId] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const formPresets = useMemo(() => presetsForKind(form.kind || 'CONTAINER'), [form.kind]);

  const selected = useMemo(
    () => templates.find((row) => row.id === selectedId) || null,
    [templates, selectedId]
  );

  const loadTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await logisticsCatalogService.listTemplates({
        kind: kindFilter || undefined,
        search: search.trim() || undefined,
        include_inactive: true,
      });
      setTemplates(rows);
      if (selectedId != null && !rows.some((r) => r.id === selectedId)) {
        setSelectedId(null);
        setForm(EMPTY_FORM);
      }
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [kindFilter, search, selectedId]);

  const loadAssignments = useCallback(async (templateId: number) => {
    try {
      const rows = await logisticsCatalogService.listAssignments({ template_id: templateId });
      setAssignments(rows);
    } catch (e) {
      setError(errorMessage(e));
    }
  }, []);

  useEffect(() => {
    void loadTemplates();
  }, [kindFilter]); // eslint-disable-line react-hooks/exhaustive-deps -- search on demand

  useEffect(() => {
    void suppliersService
      .getSuppliers()
      .then(setSuppliers)
      .catch(() => setSuppliers([]));
  }, []);

  useEffect(() => {
    if (selectedId != null) void loadAssignments(selectedId);
    else setAssignments([]);
  }, [selectedId, loadAssignments]);

  const selectTemplate = (row: LogisticsUnitTemplate) => {
    setSelectedId(row.id);
    setPresetId('');
    setForm({
      code: row.code,
      name: row.name,
      description: row.description,
      kind: row.kind,
      active: row.active,
      internal_length_mm: row.internal_length_mm,
      internal_width_mm: row.internal_width_mm,
      internal_height_mm: row.internal_height_mm,
      volume_utilization_factor: row.volume_utilization_factor,
      max_payload_kg: row.max_payload_kg,
      weight_utilization_factor: row.weight_utilization_factor,
      minimum_fill_pct: row.minimum_fill_pct,
      maximum_fill_pct: row.maximum_fill_pct,
      capacity_binding_rule: row.capacity_binding_rule,
      notes: row.notes,
    });
    setMessage(null);
    setError(null);
  };

  const startCreate = () => {
    setSelectedId(null);
    setPresetId('');
    setForm(EMPTY_FORM);
    setAssignments([]);
    setMessage(null);
    setError(null);
  };

  const applyPreset = (preset: LogisticsUnitPreset) => {
    setPresetId(preset.id);
    setForm((prev) => ({
      ...prev,
      ...preset.values,
      active: prev.active !== false,
      minimum_fill_pct: prev.minimum_fill_pct ?? null,
      maximum_fill_pct: prev.maximum_fill_pct ?? null,
      description: prev.description || '',
      notes: prev.notes || '',
    }));
    setMessage(t('logisticsCatalog.presets.applied'));
    setError(null);
  };

  const saveTemplate = async () => {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const payload: LogisticsUnitTemplatePayload = {
        ...form,
        code: (form.code || '').trim(),
        name: (form.name || '').trim(),
      };
      if (!payload.code) {
        setError(t('logisticsCatalog.errors.codeRequired'));
        return;
      }
      if (selectedId == null) {
        const created = await logisticsCatalogService.createTemplate(payload);
        setMessage(t('logisticsCatalog.messages.created'));
        await loadTemplates();
        selectTemplate(created);
      } else {
        const updated = await logisticsCatalogService.updateTemplate(selectedId, payload);
        setMessage(t('logisticsCatalog.messages.saved'));
        await loadTemplates();
        selectTemplate(updated);
      }
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const deactivateTemplate = async () => {
    if (selectedId == null) return;
    if (!window.confirm(t('logisticsCatalog.confirmDeactivate'))) return;
    setSaving(true);
    try {
      await logisticsCatalogService.deactivateTemplate(selectedId);
      setMessage(t('logisticsCatalog.messages.deactivated'));
      await loadTemplates();
      startCreate();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const assignSupplier = async () => {
    if (selectedId == null || !assignSupplierId) return;
    setSaving(true);
    setError(null);
    try {
      await logisticsCatalogService.createAssignment({
        template_id: selectedId,
        supplier_id: Number(assignSupplierId),
        role: assignRole,
      });
      setMessage(t('logisticsCatalog.messages.assigned'));
      setAssignSupplierId('');
      await loadAssignments(selectedId);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const removeAssignment = async (id: number) => {
    if (!window.confirm(t('logisticsCatalog.confirmUnassign'))) return;
    setSaving(true);
    try {
      await logisticsCatalogService.deactivateAssignment(id);
      if (selectedId != null) await loadAssignments(selectedId);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const setNum = (field: keyof LogisticsUnitTemplatePayload, raw: string) => {
    setForm((prev) => ({ ...prev, [field]: numOrNull(raw) }));
  };

  return (
    <div className="mx-auto w-full max-w-6xl space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Link to="/settings" className={btnGhostClass}>
            <ArrowLeft className="h-4 w-4" />
            {t('logisticsCatalog.backToSettings')}
          </Link>
          <h1 className="mt-2 text-xl font-semibold text-gray-900">{t('logisticsCatalog.title')}</h1>
          <p className="mt-1 text-sm text-gray-500">{t('logisticsCatalog.description')}</p>
        </div>
        <button type="button" className={btnPrimaryClass} onClick={startCreate}>
          <Plus className="mr-1.5 h-4 w-4" />
          {t('logisticsCatalog.newTemplate')}
        </button>
      </div>

      {message ? <p className="text-sm text-emerald-700">{message}</p> : null}
      {error ? <p className="text-sm text-red-600">{error}</p> : null}

      <div className="grid gap-4 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.4fr)]">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">{t('logisticsCatalog.listTitle')}</CardTitle>
            <CardDescription>{t('logisticsCatalog.listHelp')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap gap-2">
              <input
                className={`${inputClass} max-w-[12rem]`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('logisticsCatalog.searchPlaceholder')}
              />
              <select
                className={`${inputClass} max-w-[10rem]`}
                value={kindFilter}
                onChange={(e) => setKindFilter(e.target.value as LogisticsUnitKind | '')}
              >
                <option value="">{t('logisticsCatalog.kinds.all')}</option>
                <option value="PALLET">{t('logisticsCatalog.kinds.PALLET')}</option>
                <option value="CONTAINER">{t('logisticsCatalog.kinds.CONTAINER')}</option>
                <option value="TRUCK">{t('logisticsCatalog.kinds.TRUCK')}</option>
              </select>
              <button type="button" className={btnSecondaryClass} onClick={() => void loadTemplates()}>
                {t('common.search')}
              </button>
            </div>
            {loading ? (
              <p className="text-sm text-gray-500">{t('common.loading')}</p>
            ) : (
              <ul className="divide-y divide-gray-100 rounded-md border border-gray-200">
                {templates.map((row) => (
                  <li key={row.id}>
                    <button
                      type="button"
                      onClick={() => selectTemplate(row)}
                      className={`flex w-full items-start gap-2 px-3 py-2.5 text-left text-sm hover:bg-gray-50 ${
                        selectedId === row.id ? 'bg-sky-50' : ''
                      }`}
                    >
                      <Container className="mt-0.5 h-4 w-4 shrink-0 text-stocker-primary" />
                      <span className="min-w-0">
                        <span className="block font-medium text-gray-900">
                          {row.code}
                          {!row.active ? (
                            <span className="ml-2 text-xs font-normal text-amber-700">
                              {t('logisticsCatalog.inactive')}
                            </span>
                          ) : null}
                        </span>
                        <span className="block text-xs text-gray-500">
                          {t(`logisticsCatalog.kinds.${row.kind}`)}
                          {row.name ? ` · ${row.name}` : ''}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
                {templates.length === 0 ? (
                  <li className="px-3 py-4 text-sm text-gray-500">{t('logisticsCatalog.empty')}</li>
                ) : null}
              </ul>
            )}
          </CardContent>
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">
                {selectedId == null ? t('logisticsCatalog.formCreate') : t('logisticsCatalog.formEdit')}
              </CardTitle>
              <CardDescription>{t('logisticsCatalog.formHelp')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2.5">
                <label className="block text-sm">
                  <span className="mb-1 block font-medium text-gray-800">
                    {t('logisticsCatalog.presets.label')}
                  </span>
                  <select
                    className={inputClass}
                    value={presetId}
                    onChange={(e) => {
                      const id = e.target.value;
                      if (!id) {
                        setPresetId('');
                        return;
                      }
                      const preset = formPresets.find((p) => p.id === id);
                      if (preset) applyPreset(preset);
                    }}
                  >
                    <option value="">{t('logisticsCatalog.presets.placeholder')}</option>
                    {formPresets.map((p) => (
                      <option key={p.id} value={p.id}>
                        {t(`logisticsCatalog.${p.labelKey}`)}
                      </option>
                    ))}
                  </select>
                </label>
                <p className="mt-1.5 text-xs text-slate-500">
                  {presetId
                    ? t(
                        `logisticsCatalog.${
                          formPresets.find((p) => p.id === presetId)?.standardHintKey ||
                          'presets.hints.generic'
                        }`
                      )
                    : t('logisticsCatalog.presets.help')}
                </p>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block text-sm">
                  <span className="mb-1 block text-gray-700">{t('logisticsCatalog.fields.code')}</span>
                  <input
                    className={inputClass}
                    value={form.code || ''}
                    onChange={(e) => {
                      setPresetId('');
                      setForm((p) => ({ ...p, code: e.target.value }));
                    }}
                  />
                </label>
                <label className="block text-sm">
                  <span className="mb-1 block text-gray-700">{t('logisticsCatalog.fields.kind')}</span>
                  <select
                    className={inputClass}
                    value={form.kind || 'CONTAINER'}
                    onChange={(e) => {
                      setPresetId('');
                      setForm((p) => ({ ...p, kind: e.target.value as LogisticsUnitKind }));
                    }}
                  >
                    <option value="PALLET">{t('logisticsCatalog.kinds.PALLET')}</option>
                    <option value="CONTAINER">{t('logisticsCatalog.kinds.CONTAINER')}</option>
                    <option value="TRUCK">{t('logisticsCatalog.kinds.TRUCK')}</option>
                  </select>
                </label>
                <label className="block text-sm sm:col-span-2">
                  <span className="mb-1 block text-gray-700">{t('logisticsCatalog.fields.name')}</span>
                  <input
                    className={inputClass}
                    value={form.name || ''}
                    onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  />
                </label>
                <label className="block text-sm">
                  <span className="mb-1 block text-gray-700">{t('logisticsCatalog.fields.length')}</span>
                  <input
                    className={inputClass}
                    value={form.internal_length_mm ?? ''}
                    onChange={(e) => setNum('internal_length_mm', e.target.value)}
                  />
                </label>
                <label className="block text-sm">
                  <span className="mb-1 block text-gray-700">{t('logisticsCatalog.fields.width')}</span>
                  <input
                    className={inputClass}
                    value={form.internal_width_mm ?? ''}
                    onChange={(e) => setNum('internal_width_mm', e.target.value)}
                  />
                </label>
                <label className="block text-sm">
                  <span className="mb-1 block text-gray-700">{t('logisticsCatalog.fields.height')}</span>
                  <input
                    className={inputClass}
                    value={form.internal_height_mm ?? ''}
                    onChange={(e) => setNum('internal_height_mm', e.target.value)}
                  />
                </label>
                <label className="block text-sm">
                  <span className="mb-1 block text-gray-700">{t('logisticsCatalog.fields.maxPayload')}</span>
                  <input
                    className={inputClass}
                    value={form.max_payload_kg ?? ''}
                    onChange={(e) => setNum('max_payload_kg', e.target.value)}
                  />
                </label>
                <label className="block text-sm">
                  <span className="mb-1 block text-gray-700">{t('logisticsCatalog.fields.volUtil')}</span>
                  <input
                    className={inputClass}
                    value={form.volume_utilization_factor ?? ''}
                    onChange={(e) => setNum('volume_utilization_factor', e.target.value)}
                  />
                </label>
                <label className="block text-sm">
                  <span className="mb-1 block text-gray-700">{t('logisticsCatalog.fields.weightUtil')}</span>
                  <input
                    className={inputClass}
                    value={form.weight_utilization_factor ?? ''}
                    onChange={(e) => setNum('weight_utilization_factor', e.target.value)}
                  />
                </label>
                <label className="block text-sm">
                  <span className="mb-1 block text-gray-700">{t('logisticsCatalog.fields.minFill')}</span>
                  <input
                    className={inputClass}
                    value={form.minimum_fill_pct ?? ''}
                    onChange={(e) => setNum('minimum_fill_pct', e.target.value)}
                  />
                </label>
                <label className="block text-sm">
                  <span className="mb-1 block text-gray-700">{t('logisticsCatalog.fields.maxFill')}</span>
                  <input
                    className={inputClass}
                    value={form.maximum_fill_pct ?? ''}
                    onChange={(e) => setNum('maximum_fill_pct', e.target.value)}
                  />
                </label>
                <label className="block text-sm">
                  <span className="mb-1 block text-gray-700">{t('logisticsCatalog.fields.binding')}</span>
                  <select
                    className={inputClass}
                    value={form.capacity_binding_rule || 'BOTH'}
                    onChange={(e) => setForm((p) => ({ ...p, capacity_binding_rule: e.target.value }))}
                  >
                    <option value="BOTH">BOTH</option>
                    <option value="VOLUME">VOLUME</option>
                    <option value="WEIGHT">WEIGHT</option>
                  </select>
                </label>
                <label className="flex items-center gap-2 text-sm sm:col-span-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-stocker-primary"
                    checked={form.active !== false}
                    onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))}
                  />
                  {t('logisticsCatalog.fields.active')}
                </label>
              </div>
              {selected ? (
                <p className="text-xs text-gray-500">
                  {t('logisticsCatalog.derived', {
                    volume: selected.usable_volume_m3 ?? '—',
                    payload: selected.usable_payload_kg ?? '—',
                  })}
                </p>
              ) : null}
              <div className="flex flex-wrap gap-2">
                <button type="button" className={btnPrimaryClass} disabled={saving} onClick={() => void saveTemplate()}>
                  {t('common.save')}
                </button>
                {selectedId != null ? (
                  <button
                    type="button"
                    className={btnSecondaryClass}
                    disabled={saving}
                    onClick={() => void deactivateTemplate()}
                  >
                    <Trash2 className="mr-1.5 h-4 w-4" />
                    {t('logisticsCatalog.deactivate')}
                  </button>
                ) : null}
              </div>
            </CardContent>
          </Card>

          {selectedId != null ? (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">{t('logisticsCatalog.assignTitle')}</CardTitle>
                <CardDescription>{t('logisticsCatalog.assignHelp')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  <SupplierSearchSelect
                    suppliers={suppliers}
                    value={assignSupplierId}
                    onChange={setAssignSupplierId}
                    placeholder={t('logisticsCatalog.selectSupplier')}
                    searchPlaceholder={t('logisticsCatalog.searchSupplier')}
                  />
                  <select
                    className={`${inputClass} max-w-[14rem]`}
                    value={assignRole}
                    onChange={(e) => setAssignRole(e.target.value as LogisticsAssignmentRole)}
                  >
                    <option value="DEFAULT_CONTAINER">{t('logisticsCatalog.roles.DEFAULT_CONTAINER')}</option>
                    <option value="DEFAULT_PALLET">{t('logisticsCatalog.roles.DEFAULT_PALLET')}</option>
                    <option value="ALLOWED">{t('logisticsCatalog.roles.ALLOWED')}</option>
                  </select>
                  <button
                    type="button"
                    className={btnPrimaryClass}
                    disabled={saving || !assignSupplierId}
                    onClick={() => void assignSupplier()}
                  >
                    {t('logisticsCatalog.assign')}
                  </button>
                </div>
                <ul className="divide-y divide-gray-100 rounded-md border border-gray-200">
                  {assignments.map((a) => (
                    <li key={a.id} className="flex items-center justify-between gap-2 px-3 py-2 text-sm">
                      <span>
                        <span className="font-medium text-gray-900">
                          {a.supplier_code ? `${a.supplier_code} · ` : ''}
                          {a.supplier_name}
                        </span>
                        <span className="ml-2 text-xs text-gray-500">
                          {t(`logisticsCatalog.roles.${a.role}`)}
                        </span>
                      </span>
                      <button
                        type="button"
                        className="text-xs text-red-600 hover:underline"
                        onClick={() => void removeAssignment(a.id)}
                      >
                        {t('logisticsCatalog.unassign')}
                      </button>
                    </li>
                  ))}
                  {assignments.length === 0 ? (
                    <li className="px-3 py-3 text-sm text-gray-500">{t('logisticsCatalog.noAssignments')}</li>
                  ) : null}
                </ul>
              </CardContent>
            </Card>
          ) : null}
        </div>
      </div>
    </div>
  );
}
